import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPut, adminPost, adminGetBlob, downloadBlob } from '../adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Card, Field, GREEN, mono } from './bfsiShared';

export default function BfsiComplianceTab({ tenantId }) {
  const [comp, setComp] = useState(null);
  const [rules, setRules] = useState([]);
  const [docs, setDocs] = useState([]);
  const [doc, setDoc] = useState({ doc_type: 'VAPT_REPORT', title: '', content_text: '' });
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}/compliance`).then(setComp).catch(e => toast.error(e.message));
    adminGet(`/customers/${tenantId}/approval-rules`).then(setRules).catch(() => {});
    adminGet(`/compliance-documents?tenant_id=${tenantId}`).then(setDocs).catch(() => {});
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const events = comp.recent_sensitive_events;
      const r = await adminPut(`/customers/${tenantId}/compliance`, comp);
      setComp({ ...r, recent_sensitive_events: events });
      toast.success('Compliance profile saved');
    } catch (e) { toast.error(e.message); }
  };

  const addDoc = async () => {
    try {
      await adminPost('/compliance-documents', { tenant_id: tenantId, ...doc });
      toast.success('Document added'); setDoc({ doc_type: 'VAPT_REPORT', title: '', content_text: '' }); load();
    } catch (e) { toast.error(e.message); }
  };

  const evidencePack = async () => {
    setDownloading(true);
    try {
      const blob = await adminGetBlob(`/customers/${tenantId}/evidence-pack`);
      downloadBlob(blob, 'evidence-pack.zip');
      toast.success('Evidence pack downloaded');
    } catch (e) { toast.error(e.message); }
    setDownloading(false);
  };

  if (!comp) return <div className="text-sm" style={{ color: '#9a9e96' }}>Loading…</div>;
  const c = (k) => (v) => setComp({ ...comp, [k]: v });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bfsi-compliance-tab">
      <div className="space-y-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold" style={{ color: '#1B1D1C' }}>Compliance profile</h3>
            <Button data-testid="evidence-pack-btn" onClick={evidencePack} disabled={downloading}
              variant="outline" className="bg-white border-[#D9D6CD]">
              {downloading ? 'Building…' : 'Download evidence pack'}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Data retention (years)" value={comp.data_retention_years} onChange={c('data_retention_years')} testId="comp-retention" type="number" />
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Borrower PII stored</label>
              <div className="h-9 flex items-center text-sm font-medium" style={{ color: GREEN }} data-testid="comp-pii">
                No · loan reference only (enforced)
              </div>
            </div>
            <Field label="Security incident contact" value={comp.security_incident_contact} onChange={c('security_incident_contact')} testId="comp-incident-contact" />
            <Field label="Breach notice (hours)" value={comp.breach_notice_hours} onChange={c('breach_notice_hours')} testId="comp-breach-hours" type="number" />
            <Field label="Last VAPT report" value={comp.last_vapt_date ? comp.last_vapt_date.slice(0, 10) : ''} onChange={c('last_vapt_date')} testId="comp-vapt-date" type="date" />
            <Field label="Last DR drill" value={comp.last_dr_drill_date ? comp.last_dr_drill_date.slice(0, 10) : ''} onChange={c('last_dr_drill_date')} testId="comp-dr-date" type="date" />
          </div>
          <Button data-testid="comp-save-btn" onClick={save} className="mt-5 text-white" style={{ background: GREEN }}>Save</Button>
        </Card>

        <Card>
          <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Compliance documents</h3>
          <ul className="text-sm mb-4 space-y-1">
            {docs.length === 0 && <li style={{ color: '#9a9e96' }}>No documents uploaded</li>}
            {docs.map(d => (
              <li key={d.id} className="flex justify-between border-t py-1.5" style={{ borderColor: '#F0EEE8' }}>
                <span>{d.title} <span className="text-xs" style={{ color: '#9a9e96' }}>({d.doc_type})</span></span>
                <span className="text-xs" style={{ color: '#9a9e96' }}>{new Date(d.uploaded_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Type</label>
              <select data-testid="doc-type-select" value={doc.doc_type} onChange={e => setDoc({ ...doc, doc_type: e.target.value })}
                className="w-full h-9 rounded-md border px-3 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
                <option value="VAPT_REPORT">VAPT report</option>
                <option value="DR_DRILL">DR drill record</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <Field label="Title" value={doc.title} onChange={v => setDoc({ ...doc, title: v })} testId="doc-title-input" />
          </div>
          <textarea data-testid="doc-content-input" value={doc.content_text} onChange={e => setDoc({ ...doc, content_text: e.target.value })}
            rows={2} placeholder="Document notes / summary" className="w-full mt-3 rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#D9D6CD' }} />
          <Button data-testid="doc-add-btn" onClick={addDoc} disabled={!doc.title.trim()} className="mt-3 text-white" style={{ background: GREEN }}>Add document</Button>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Maker-checker rules</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
                <th className="py-2">Action</th><th className="py-2">Approvers</th><th className="py-2">Count</th><th className="py-2">E-sign</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.action_type} className="border-t" style={{ borderColor: '#F0EEE8' }}>
                  <td className="py-2" style={mono}>{r.action_type}</td>
                  <td className="py-2" style={{ color: '#6b7069' }}>{(r.approver_roles || []).join(' + ')}</td>
                  <td className="py-2">{r.approvals_required}</td>
                  <td className="py-2">{r.esign_required ? 'Yes + reason' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card data-testid="sensitive-events-card">
          <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Recent sensitive events</h3>
          <ul className="text-sm space-y-1.5">
            {(comp.recent_sensitive_events || []).length === 0 && <li style={{ color: '#9a9e96' }}>No events yet</li>}
            {(comp.recent_sensitive_events || []).map((e, i) => (
              <li key={`${e.at}-${e.action}-${i}`} className="flex justify-between border-t py-1.5" style={{ borderColor: '#F0EEE8' }}>
                <span style={mono} className="text-xs">{e.action}</span>
                <span className="text-xs" style={{ color: '#9a9e96' }}>{new Date(e.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
